import React, { useState, useRef, useEffect } from 'react';
import { EditableField } from './EditableField';
import { EditableDateField } from './EditableDateField';
import { LogoUploader } from './LogoUploader';
import { InvoiceLineItems } from './InvoiceLineItems';
import { InvoiceCalculations } from './InvoiceCalculations';
import { InvoicePreviewModal } from './InvoicePreviewModal';
import { InvoiceV2State, generateInvoicePDF, printInvoice, generateInvoicePreviewUrl, generateInvoicePDFFromData, generateInvoiceHTML } from '../../utils/invoiceV2PdfGenerator';
import { formatPhoneNumber } from '../../utils/formatters';
import { Button } from '../ui/button';
import { Download, Printer, Eye, Save, Building2 } from 'lucide-react';

interface InvoiceEditorV2Props {
  initialData?: Partial<InvoiceV2State>;
  onSave?: (data: InvoiceV2State) => void;
  onManualSave?: (data: InvoiceV2State) => void;
  onChange?: (data: InvoiceV2State) => void; // Notify parent of state changes
  isLoading?: boolean;
  isNewInvoice?: boolean; // Add flag to distinguish new vs existing invoices
  onLoadCompanyData?: () => void; // Add callback to load company data
  forceUpdateItems?: boolean; // Force update items when billable items are loaded
  isReadOnly?: boolean; // Add read-only mode for locked invoices
  isSaving?: boolean; // Add flag to prevent auto-save during manual saves
}

export const InvoiceEditorV2: React.FC<InvoiceEditorV2Props> = ({
  initialData,
  onSave,
  onManualSave,
  onChange,
  isLoading = false,
  isNewInvoice = false,
  onLoadCompanyData,
  forceUpdateItems = false,
  isReadOnly = false,
  isSaving = false
}) => {
  const invoiceRef = useRef<HTMLDivElement>(null);
  const [showPreview, setShowPreview] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string>('');
  const [previewHtml, setPreviewHtml] = useState<string>('');
  const [isInitialLoad, setIsInitialLoad] = useState(true);

  // Wrapper function to update invoice data
  const updateInvoiceData = (updater: (prev: InvoiceV2State) => InvoiceV2State) => {
    setInvoiceData(prev => {
      const newData = updater(prev);
      // Notify parent of changes
      onChange?.(newData);
      return newData;
    });
  };

  // Strip HTML tags for display
  const stripHtml = (html: string) => {
    if (!html) return '';
    // Remove HTML tags using regex and decode HTML entities
    return html
      .replace(/<[^>]*>/g, '') // Remove HTML tags
      .replace(/&nbsp;/g, ' ') // Replace &nbsp; with space
      .replace(/&amp;/g, '&') // Replace &amp; with &
      .replace(/&lt;/g, '<') // Replace &lt; with <
      .replace(/&gt;/g, '>') // Replace &gt; with >
      .replace(/&quot;/g, '"') // Replace &quot; with "
      .trim();
  };

  const [invoiceData, setInvoiceData] = useState<InvoiceV2State>({
    logo: '',
    companyName: '',
    companyAddress: '',
    companyPhone: '',
    companyEmail: '',
    companyWebsite: '',
    recipientName: '',
    recipientAddress: '',
    recipientPhone: '',
    recipientEmail: '',
    invoiceNumber: '',
    invoiceDate: new Date(),
    dueDate: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000), // 30 days from now
    items: [
      {
        id: '1',
        description: '',
        quantity: 1,
        rate: 0
      }
    ],
    taxRate: 0.10, // 10%
    discountAmount: 0,
    discountType: 'None' as const,
    discountPercentage: 0,
    discountDescription: '',
    amountPaid: 0,
    paymentInfo: '',
    terms: '',
    notes: '',
    ...initialData
  });

  // Update invoice data when initialData changes (e.g., when company data loads)
  useEffect(() => {
    // Don't update if we're past initial load (prevents loop after saves)
    if (!isInitialLoad && !forceUpdateItems) {
      return;
    }

    if (initialData && Object.keys(initialData).length > 0) {
      setInvoiceData(prev => {
        // If forceUpdateItems is true, always use the new items
        if (forceUpdateItems && initialData.items) {
          const newData = { ...prev, ...initialData };
          return newData;
        }

        // Don't override items if they already exist (to preserve billable items)
        const newData = { ...prev, ...initialData };
        if (prev.items && prev.items.length > 0 && initialData.items) {
          // If we have existing items and new items, merge them intelligently
          const existingItemIds = new Set(prev.items.map(item => item.id));
          const newItems = initialData.items.filter(item => !existingItemIds.has(item.id));
          newData.items = [...prev.items, ...newItems];
        }
        return newData;
      });
    }

    // Set initial load to false after first update
    if (isInitialLoad) {
      setIsInitialLoad(false);
    }
  }, [initialData, forceUpdateItems, isInitialLoad]);

  const handleDownloadPDF = async () => {
    try {
      // Debug: Log invoice data before generating PDF
      console.log('🔍 PDF Generation - Invoice Data:', invoiceData);
      console.log('🔍 PDF Generation - Discount Amount:', invoiceData.discountAmount);
      console.log('🔍 PDF Generation - Discount Type:', invoiceData.discountType);
      console.log('🔍 PDF Generation - Discount Percentage:', invoiceData.discountPercentage);
      console.log('🔍 PDF Generation - Discount Description:', invoiceData.discountDescription);

      // Use the same clean PDF generation as the grid
      await generateInvoicePDFFromData(invoiceData, invoiceData.invoiceNumber);
    } catch (error) {
      console.error('Error downloading PDF:', error);
    }
  };

  const handlePrint = () => {
    if (!invoiceRef.current) return;
    
    try {
      printInvoice(invoiceRef.current);
    } catch (error) {
      console.error('Error printing:', error);
    }
  };

  const handlePreview = async () => {
    try {
      // Use the same clean HTML generation as the invoices grid
      const invoiceHtml = generateInvoiceHTML(invoiceData);
      setPreviewHtml(invoiceHtml);
      setShowPreview(true);
    } catch (error) {
      console.error('Error generating preview:', error);
    }
  };

  const closePreview = () => {
    setShowPreview(false);
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
    }
    setPreviewUrl('');
    setPreviewHtml('');
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-accent-color mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-gray-400">Loading invoice...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto p-6">
      {/* Action Buttons */}
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-gray-800 dark:text-gray-100">Invoice Editor</h1>
        <div className="flex space-x-3">
          {isNewInvoice && (
            <Button
              onClick={() => onManualSave?.(invoiceData)}
              variant="outline"
              className="text-green-700 border-green-300 hover:bg-green-50 dark:text-green-300 dark:border-green-600 dark:hover:bg-green-900/20"
            >
              <Save className="h-4 w-4 mr-2" />
              Save Invoice
            </Button>
          )}
          {isNewInvoice && onLoadCompanyData && (
            <Button
              onClick={onLoadCompanyData}
              variant="outline"
            >
              <Building2 className="h-4 w-4 mr-2" />
              Load Company Data
            </Button>
          )}
          <Button
            onClick={handlePreview}
            variant="outline"
          >
            <Eye className="h-4 w-4 mr-2" />
            Preview PDF
          </Button>
          <Button
            onClick={handlePrint}
            variant="outline"
          >
            <Printer className="h-4 w-4 mr-2" />
            Print
          </Button>
          <Button
            onClick={handleDownloadPDF}
            variant="default"
          >
            <Download className="h-4 w-4 mr-2" />
            Download PDF
          </Button>
        </div>
      </div>

      {/* Invoice Container */}
      <div
        ref={invoiceRef}
        className="bg-white border border-gray-200 rounded-lg shadow-sm mx-auto"
        style={{ 
          maxWidth: '8.5in',
          padding: '48px',
          fontFamily: 'Arial, sans-serif',
          lineHeight: '1.4',
          minHeight: '11in'
        }}
      >
        {/* Header */}
        <div className="flex justify-between items-start mb-8">
          <div>
            {/* Logo above company name */}
            <div className="mb-4">
              <LogoUploader
                logo={invoiceData.logo}
                onChange={(logo) => updateInvoiceData(prev => ({ ...prev, logo }))}
              />
            </div>
            
            {/* Company info */}
            <EditableField
              value={invoiceData.companyName}
              onChange={(value) => updateInvoiceData(prev => ({ ...prev, companyName: value }))}
              placeholder="Company Name"
              className="text-xl font-bold text-gray-800 mb-2"
              disabled={isReadOnly}
            />
            <EditableField
              value={invoiceData.companyAddress}
              onChange={(value) => updateInvoiceData(prev => ({ ...prev, companyAddress: value }))}
              placeholder="Company Address"
              multiline
              className="text-gray-600"
              disabled={isReadOnly}
            />
            <EditableField
              value={formatPhoneNumber(invoiceData.companyPhone)}
              onChange={(value) => {
                // Remove formatting when editing
                const unformatted = value.replace(/\D/g, '');
                updateInvoiceData(prev => ({ ...prev, companyPhone: unformatted }));
              }}
              placeholder="Phone Number"
              className="text-sm text-gray-600 mt-1"
              disabled={isReadOnly}
            />
            <EditableField
              value={invoiceData.companyEmail}
              onChange={(value) => updateInvoiceData(prev => ({ ...prev, companyEmail: value }))}
              placeholder="Email Address"
              className="text-sm text-gray-600 mt-1"
              disabled={isReadOnly}
            />
            <EditableField
              value={invoiceData.companyWebsite}
              onChange={(value) => updateInvoiceData(prev => ({ ...prev, companyWebsite: value }))}
              placeholder="Website"
              className="text-sm text-gray-600 mt-1"
              disabled={isReadOnly}
            />
            
            {/* Bill To section under company info */}
            <div className="mt-6">
              <h3 className="font-semibold text-gray-800 mb-2">BILL TO:</h3>
              <EditableField
                value={invoiceData.recipientName}
                onChange={(value) => updateInvoiceData(prev => ({ ...prev, recipientName: value }))}
                placeholder="Client Name"
                className="font-semibold text-gray-800 mb-2"
                disabled={isReadOnly}
              />
              <EditableField
                value={invoiceData.recipientAddress}
                onChange={(value) => updateInvoiceData(prev => ({ ...prev, recipientAddress: value }))}
                placeholder="Client Address"
                multiline
                className="text-sm text-gray-600"
                disabled={isReadOnly}
              />
              <EditableField
                value={formatPhoneNumber(invoiceData.recipientPhone)}
                onChange={(value) => {
                  // Remove formatting when editing
                  const unformatted = value.replace(/\D/g, '');
                  updateInvoiceData(prev => ({ ...prev, recipientPhone: unformatted }));
                }}
                placeholder="Phone Number"
                className="text-sm text-gray-600 mt-1"
                disabled={isReadOnly}
              />
              <EditableField
                value={invoiceData.recipientEmail}
                onChange={(value) => updateInvoiceData(prev => ({ ...prev, recipientEmail: value }))}
                placeholder="Email Address"
                className="text-sm text-gray-600 mt-1"
                disabled={isReadOnly}
              />
            </div>
          </div>
          
          <div className="text-right">
            <h2 className="text-2xl font-bold text-gray-800 mb-4">INVOICE</h2>
            
            <div className="space-y-2">
              <div className="flex justify-between space-x-8">
                <span className="text-gray-600">Invoice No:</span>
                <EditableField
                  value={invoiceData.invoiceNumber}
                  onChange={(value) => updateInvoiceData(prev => ({ ...prev, invoiceNumber: value }))}
                  placeholder="INV-001"
                  className="font-medium"
                  disabled={isReadOnly}
                />
              </div>
              <div className="flex justify-between space-x-8">
                <span className="text-gray-600">Invoice Date:</span>
                <EditableDateField
                  value={invoiceData.invoiceDate}
                  onChange={(date) => updateInvoiceData(prev => ({ ...prev, invoiceDate: date }))}
                  className="font-medium"
                />
              </div>
              <div className="flex justify-between space-x-8">
                <span className="text-gray-600">Due Date:</span>
                <EditableDateField
                  value={invoiceData.dueDate}
                  onChange={(date) => updateInvoiceData(prev => ({ ...prev, dueDate: date }))}
                  className="font-medium"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Line Items */}
        <div className="mb-8">
          <InvoiceLineItems
            items={invoiceData.items}
            onChange={(items) => updateInvoiceData(prev => ({ ...prev, items }))}
          />
        </div>

        {/* Calculations */}
        <div className="flex justify-end mb-8">
          <div className="w-80">
            <InvoiceCalculations
              items={invoiceData.items}
              taxRate={invoiceData.taxRate}
              discountAmount={invoiceData.discountAmount}
              discountType={invoiceData.discountType}
              discountPercentage={invoiceData.discountPercentage}
              amountPaid={invoiceData.amountPaid}
              onTaxRateChange={(rate) => updateInvoiceData(prev => ({ ...prev, taxRate: rate }))}
              onDiscountAmountChange={(amount) => updateInvoiceData(prev => ({ ...prev, discountAmount: amount }))}
              onDiscountTypeChange={(type) => updateInvoiceData(prev => ({ ...prev, discountType: type }))}
              onDiscountPercentageChange={(percentage) => updateInvoiceData(prev => ({ ...prev, discountPercentage: percentage }))}
              onAmountPaidChange={(amount) => updateInvoiceData(prev => ({ ...prev, amountPaid: amount }))}
              clientLocationName={invoiceData.clientLocationName as any}
              isUsingLocationTaxRate={Boolean(invoiceData.clientLocationName)}
            />
          </div>
        </div>

        {/* Footer - Linear layout like PDF */}
        <div className="space-y-6">
          {/* Payment Info */}
          <div>
            <h3 className="font-semibold text-gray-800 mb-2">PAYMENT INFORMATION:</h3>
            <textarea
              value={invoiceData.paymentInfo || ''}
              onChange={(e) => setInvoiceData(prev => ({ ...prev, paymentInfo: e.target.value }))}
              onBlur={() => onChange?.(invoiceData)}
              placeholder="Enter payment information"
              className="w-full p-2 border border-gray-300 rounded text-sm text-gray-600"
              rows={3}
              disabled={isReadOnly}
            />
          </div>

          {/* Terms & Conditions */}
          <div>
            <h3 className="font-semibold text-gray-800 mb-2">TERMS & CONDITIONS:</h3>
            <textarea
              value={invoiceData.terms || ''}
              onChange={(e) => setInvoiceData(prev => ({ ...prev, terms: e.target.value }))}
              onBlur={() => onChange?.(invoiceData)}
              placeholder="Enter terms and conditions"
              className="w-full p-2 border border-gray-300 rounded text-sm text-gray-600"
              rows={3}
              disabled={isReadOnly}
            />
          </div>

          {/* Notes */}
          <div>
            <h3 className="font-semibold text-gray-800 mb-2">NOTES:</h3>
            <textarea
              value={invoiceData.notes || ''}
              onChange={(e) => setInvoiceData(prev => ({ ...prev, notes: e.target.value }))}
              onBlur={() => onChange?.(invoiceData)}
              placeholder="Enter notes"
              className="w-full p-2 border border-gray-300 rounded text-sm text-gray-600"
              rows={3}
              disabled={isReadOnly}
            />
          </div>
        </div>
      </div>

      {/* Preview Modal */}
      <InvoicePreviewModal
        isOpen={showPreview}
        onClose={closePreview}
        pdfUrl={previewUrl}
        htmlContent={previewHtml}
      />
    </div>
  );
};
